import React, { useState, useEffect } from 'react';
import { StyleSheet, ScrollView, KeyboardAvoidingView, Platform } from 'react-native';
import { Modal, Portal, Text, TextInput, Button, Card } from 'react-native-paper';
import { colors, spacing, typography } from '../constants/theme';
import { isDetoxTestControlsEnabled } from '../config/environments';
import HandwritingInput from './HandwritingInput';

interface OnDemandProjectsModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSave: (onDemandProjects: string) => void;
  initialOnDemandProjects?: string;
  loading?: boolean;
}

export const OnDemandProjectsModal: React.FC<OnDemandProjectsModalProps> = ({
  visible,
  onDismiss,
  onSave,
  initialOnDemandProjects = '',
  loading = false,
}) => {
  const [onDemandProjects, setOnDemandProjects] = useState(initialOnDemandProjects);
  const [showHandwriting, setShowHandwriting] = useState(false);

  useEffect(() => {
    setOnDemandProjects(initialOnDemandProjects);
  }, [initialOnDemandProjects, visible]);

  const handleSave = () => {
    onSave(onDemandProjects);
    onDismiss();
  };

  const handleCancel = () => {
    setOnDemandProjects(initialOnDemandProjects);
    onDismiss();
  };

  const handleHandwritingComplete = (handwrittenText: string) => {
    const updatedProjects = onDemandProjects ? `${onDemandProjects}\n${handwrittenText}` : handwrittenText;
    setOnDemandProjects(updatedProjects);
    setShowHandwriting(false);
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={handleCancel}
        contentContainerStyle={styles.container}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardView}
        >
          <Card testID="on-demand-projects-modal" style={styles.card}>
            <Card.Title
              title="On Demand Projects"
              right={() => (
                isDetoxTestControlsEnabled ? (
                  <Button
                    testID="on-demand-projects-save-button-top"
                    mode="contained"
                    compact
                    onPress={handleSave}
                    loading={loading}
                    disabled={loading}
                    style={styles.detoxSaveButton}
                  >
                    Save
                  </Button>
                ) : null
              )}
            />
            <Card.Content style={styles.content}>
              <Text style={styles.description}>
                Add review-level on demand project notes. These notes will be included in the PDF report.
              </Text>
              <ScrollView style={styles.scrollView}>
                <TextInput
                  testID="on-demand-projects-input"
                  mode="outlined"
                  multiline
                  numberOfLines={10}
                  value={onDemandProjects}
                  onChangeText={setOnDemandProjects}
                  placeholder="Enter on demand projects here..."
                  style={styles.textInput}
                  disabled={loading}
                />
              </ScrollView>
              {Platform.OS !== 'web' && (
                <Button
                  mode="outlined"
                  onPress={() => setShowHandwriting(true)}
                  icon="draw"
                  style={styles.stylusButton}
                  disabled={loading}
                >
                  Add Projects with Stylus
                </Button>
              )}
            </Card.Content>
            <Card.Actions style={styles.actions}>
              <Button
                testID="on-demand-projects-cancel-button"
                mode="text"
                onPress={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                testID="on-demand-projects-save-button"
                mode="contained"
                onPress={handleSave}
                loading={loading}
                disabled={loading}
              >
                Save Projects
              </Button>
            </Card.Actions>
          </Card>
        </KeyboardAvoidingView>
      </Modal>
      {showHandwriting && (
        <HandwritingInput
          visible={showHandwriting}
          onClose={() => setShowHandwriting(false)}
          onTextConverted={handleHandwritingComplete}
          placeholder="Write additional projects here..."
        />
      )}
    </Portal>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    margin: 20,
    borderRadius: 8,
    maxHeight: '85%',
    minHeight: 400,
  },
  keyboardView: {
    maxHeight: '100%',
    minHeight: 400,
  },
  card: {
    backgroundColor: colors.surface,
    maxHeight: '100%',
    overflow: 'hidden',
  },
  content: {
    flexShrink: 1,
  },
  description: {
    ...typography.body2,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  scrollView: {
    flexShrink: 1,
    maxHeight: 220,
  },
  textInput: {
    backgroundColor: colors.surface,
    minHeight: 160,
  },
  actions: {
    justifyContent: 'flex-end',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
    paddingTop: spacing.sm,
    flexShrink: 0,
  },
  stylusButton: {
    marginTop: spacing.sm,
    borderColor: colors.primary,
  },
  detoxSaveButton: {
    marginRight: spacing.sm,
  },
});
